// viewportDrop — what dropping a content-browser asset onto the 3D viewport means.
//
// Dragging an environment into the scene is the gesture every DCC has: the
// content browser already makes its cards draggable (AssetCard sets
// `text/aos-asset-id`), and folders already accept them, so the viewport was the
// one obvious target that did nothing. Dropping one there now does exactly what
// the card's "Apply environment" context item does — same applyEnvironment()
// call, same live-view flip, no second code path to drift.
//
// The resolution is a pure function so the interesting cases are testable
// without a browser drag: an id that isn't in the registry any more, and an
// asset that IS found but isn't an environment. Both must FAIL LOUDLY — a drop
// that silently does nothing reads as a broken drop target.
//
// No imports — leaf module (tests/unit/viewportDrop.test.mjs drives it directly).

/** The MIME type AssetCard puts the dragged asset's id under. */
export const ASSET_DRAG_TYPE = 'text/aos-asset-id'

/**
 * Is this drag something the viewport should accept?
 *
 * Reads `dataTransfer.types` rather than getData(), because during dragover the
 * payload is deliberately unreadable (browsers only expose it on drop) — the
 * type list is all a dragover handler ever gets.
 *
 * @param {readonly string[]|DOMStringList|undefined} types
 */
export function isAssetDrag(types) {
  if (!types) return false
  // DOMStringList has `contains`; a real array has `includes`.
  if (typeof types.includes === 'function') return types.includes(ASSET_DRAG_TYPE)
  if (typeof types.contains === 'function') return types.contains(ASSET_DRAG_TYPE)
  return Array.from(types).indexOf(ASSET_DRAG_TYPE) >= 0
}

/**
 * Decide what a dropped asset id means.
 *
 * @param {string} assetId
 * @param {Array<{id: string, kind: string, name?: string}>} assets  assetRegistry.list()
 * @returns {{ok: true, asset: object} | {ok: false, reason: string, message: string}}
 */
export function resolveDroppedEnvironment(assetId, assets = []) {
  if (!assetId) {
    return { ok: false, reason: 'no-id', message: 'Nothing was dropped' }
  }
  const asset = Array.isArray(assets) ? assets.find(a => a?.id === assetId) : null
  if (!asset) {
    // The card was dragged from a list that has since been refreshed, or the
    // asset was deleted mid-drag.
    return { ok: false, reason: 'not-found', message: 'That asset is no longer in the library' }
  }
  if (asset.kind !== 'environment') {
    const what = asset.name ? `“${asset.name}”` : 'That asset'
    return {
      ok: false,
      reason: 'wrong-kind',
      // Names the KIND, so the user learns which cards this target accepts
      // rather than just being told no.
      message: `${what} is a ${asset.kind} — only environments can be dropped into the scene`,
    }
  }
  return { ok: true, asset }
}
